import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import SuperAdminSidebar from '../../components/Layout/SuperAdminSidebar';
import superAdminService from '../../services/superAdminService';

// Material UI components
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Breadcrumbs,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  FormHelperText
} from '@mui/material';

// Material UI icons
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const SuperAdminAddTenantAdminPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const password = watch('password');
  
  useEffect(() => {
    const loadTenant = async () => {
      try {
        setTenantLoading(true);
        const tenantData = await superAdminService.getTenantById(tenantId);
        setTenant(tenantData);
      } catch (err) {
        setError(`Could not load tenant: ${err.message}`);
      } finally {
        setTenantLoading(false);
      }
    };
    
    loadTenant();
  }, [tenantId]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Prepare admin data
      const adminData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        role: 'admin',
        tenantId: tenantId,
        status: 'active'
      };
      
      // Call API to create admin
      const result = await superAdminService.createTenantAdmin(tenantId, adminData);
      
      setSuccess(`Admin ${data.firstName} ${data.lastName} created successfully`);
      
      // After brief delay, navigate back to tenant detail page
      setTimeout(() => {
        navigate(`/superadmin/tenants/${tenantId}`);
      }, 1500);
      
    } catch (err) {
      console.error('Error creating admin:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(`Failed to create admin: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading) {
    return (
      <Container maxWidth="lg">
        <Box my={4} textAlign="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box my={4}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            to="/superadmin/tenants"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            Organizations
          </Link>
          <Link 
            to={`/superadmin/tenants/${tenantId}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {tenant?.name || 'Organization Details'}
          </Link>
          <Typography color="text.primary">Add Admin</Typography>
        </Breadcrumbs>
        
        <Typography variant="h4" fontWeight="bold">
          Add Administrator
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add a new admin user to {tenant?.name || 'this organization'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2, borderTop: '4px solid #3f51b5' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="h6" gutterBottom>
            Admin Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                {...register('firstName', { 
                  required: 'First name is required' 
                })}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                {...register('lastName', { 
                  required: 'Last name is required' 
                })}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message: 'Password must contain uppercase, lowercase, number and special character'
                  }
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormHelperText>
                Password must be at least 8 characters and include uppercase, lowercase, 
                numbers and special characters.
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: value => 
                    value === password || 'Passwords do not match'
                })}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/superadmin/tenants/${tenantId}`)}
              startIcon={<ArrowBackIcon />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              {loading ? 'Adding...' : 'Add Administrator'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default SuperAdminAddTenantAdminPage;
